import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { TaskPage } from './pages/TaskPage';
import { PeoplePage } from './pages/PeoplePage';
import { CompaniesPage } from './pages/CompaniesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { RapidContactPage } from './pages/RapidContactPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import RapidContactList from '../entities/rapidContact/RapidContactList';
import BusinessDetail from '../entities/business/BusinessDetail';
import PersonDetail from '../entities/person/PersonDetail';
import MessageInbox from '../entities/message/MessageInbox';
import OutgoingMessageList from '../entities/outgoingMessage/OutgoingMessageList';
import { DashboardPage } from './pages/DashboardPage';
import { AutomationsPage } from './pages/AutomationsPage';
import { WhatsAppPage } from './pages/WhatsAppPage';
import { MapPage } from './pages/MapPage';
import AutomationGallery from '../entities/automation/AutomationGallery';
import AutomationForm from '../entities/automation/AutomationForm';
import AutomationSimulation from '../entities/automation/AutomationSimulation';
import AutomationMonitoring from '../entities/automation/AutomationMonitoring';
import QuotationList from '../entities/quotation/QuotationList';
import QuotationDetail from '../entities/quotation/QuotationDetail';
import QuotationForm from '../entities/quotation/QuotationForm';
import PipelineForm from '../entities/pipeline/PipelineForm';
import KanbanList from '../entities/kanban/KanbanList';
import KanbanCreate from '../entities/kanban/KanbanCreate';
import KanbanBoardPage from '../entities/kanban/KanbanBoardPage';

export const publicRoutes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
  { path: '/terms-of-service', element: <TermsOfServicePage /> },
];

export const protectedCoreRoutes: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: '/tasks', element: <TaskPage /> },
  { path: '/dashboard', element: <DashboardPage /> },
  { path: '/contacts', element: <PeoplePage /> },
  { path: '/contacts/:id', element: <PersonDetail /> },
  // Sotto-tab attività deep-linkabili (notes/emails/calls/meetings/tasks); base = "Tutte".
  //
  // Lo `*` finale matcha anche ZERO segmenti, quindi questa rotta copre sia
  // `/contacts/1/notes` sia `/contacts/1/demo-altro/scheda/3`. Serve alle tab
  // contribuite dai plugin (slot `detail-center:activity-tab`): con il path
  // terminato da splat, il componente del contributo può rendere un proprio
  // `<Routes>` e le sue sotto-rotte si risolvono RELATIVE a questo punto, con la
  // scheda e la striscia dei tipi ancora intorno. Senza lo splat, React Router
  // rifiuta le descendant routes e un URL più profondo cade sul 404.
  //
  // Il core non conosce quelle sotto-rotte e non deve: le dichiara il plugin.
  // Per questo il contratto `SlotContributor` non cambia.
  { path: '/contacts/:id/:section/*', element: <PersonDetail /> },
  // Deep-link a un commento specifico: apre il tab, espande l'attività, evidenzia il commento.
  { path: '/contacts/:id/:section/:activityId/comments/:commentId', element: <PersonDetail /> },
  { path: '/rapid-contacts', element: <RapidContactList /> },
  { path: '/add-rapid-contact', element: <RapidContactPage /> },
  // Dettaglio/edit di un contatto fiera: la stessa form di inserimento, riusata in
  // modalità edit (imported=false, salva -> update) o sola lettura (imported=true).
  { path: '/rapid-contact/:rapidContactId', element: <RapidContactPage /> },
  { path: '/companies', element: <CompaniesPage /> },
  { path: '/companies/:id', element: <BusinessDetail /> },
  // Sotto-tab attività deep-linkabili; base = "Tutte". Sullo `*` finale e sulle
  // sotto-rotte dei plugin, vedi il commento su `/contacts/:id/:section/*`.
  { path: '/companies/:id/:section/*', element: <BusinessDetail /> },
  // Deep-link a un commento specifico: apre il tab, espande l'attività, evidenzia il commento.
  { path: '/companies/:id/:section/:activityId/comments/:commentId', element: <BusinessDetail /> },
  { path: '/inbox', element: <MessageInbox /> },
  // La Sorveglianza (FILO-58): oggi è la coda delle mail in uscita, con la sua voce di menu e il
  // link della Smart Inbox. Sta fuori da `/inbox` perché si spegne per conto suo: sotto quel
  // prefisso la chiuderebbe anche la Smart Inbox spenta. Per lo stesso motivo il vecchio
  // `/inbox/outgoing` non reindirizza: con la Inbox spenta il redirect non verrebbe raggiunto.
  { path: '/surveillance', element: <OutgoingMessageList /> },
  // ─── Preventivi ───
  // Sono schermate di prodotto da TS-87: elenco, dettaglio e form stanno qui, e
  // la voce di menu la dichiara la Sidebar del core.
  //
  // Il plugin trentino conserva una sola rotta sotto questo prefisso,
  // `/quotations/:id/dxf`, e non collide: `useRoutes` ordina per SPECIFICITA',
  // non per posizione nell'array, quindi il segmento statico `dxf` batte il
  // `:section` dichiarato qui sotto da qualunque parte arrivi. L'ordine
  // dell'array decide solo fra path identici.
  { path: '/quotations', element: <QuotationList /> },
  // I segmenti statici vincono sui dinamici a parità di profondità, quindi
  // '/new' ed '/edit' non vengono catturati da ':id' e ':section'.
  { path: '/quotations/new', element: <QuotationForm /> },
  { path: '/quotations/:id/edit', element: <QuotationForm /> },
  { path: '/quotations/:id', element: <QuotationDetail /> },
  // Sotto-tab attività deep-linkabili; base = "Tutte". Sullo `*` finale e sulle
  // sotto-rotte dei plugin, vedi il commento su `/contacts/:id/:section/*`.
  { path: '/quotations/:id/:section/*', element: <QuotationDetail /> },
  // Deep-link a un commento specifico: apre il tab, espande l'attività, evidenzia il commento.
  { path: '/quotations/:id/:section/:activityId/comments/:commentId', element: <QuotationDetail /> },
  // ─── Kanban ───
  // Elenco delle bacheche + wizard di creazione + board (/kanban/:id). Il
  // segmento statico '/new' vince sul dinamico ':id'.
  { path: '/kanban', element: <KanbanList /> },
  { path: '/kanban/new', element: <KanbanCreate /> },
  { path: '/kanban/:id', element: <KanbanBoardPage /> },
  // FILO-37: il client WhatsApp; il segmento è il contatto della conversazione aperta.
  { path: '/whatsapp', element: <WhatsAppPage /> },
  { path: '/whatsapp/:personId', element: <WhatsAppPage /> },
  { path: '/map', element: <MapPage /> },
  { path: '/automations', element: <AutomationsPage /> },
  // ─── Automazione singola ───
  // View SOLA LETTURA della configurazione (qualunque stato), resa da AutomationForm in modo readOnly.
  { path: '/automations/:id', element: <AutomationForm /> },
  // Edit (solo DRAFT): se il record non è DRAFT, AutomationForm reindirizza alla view read-only /automations/:id.
  { path: '/automations/:id/edit', element: <AutomationForm /> },
  { path: '/automations/:id/monitoring', element: <AutomationMonitoring /> },
  { path: '/automations/:id/simulation', element: <AutomationSimulation /> },
  // ─── Template ───
  // Gallery dei blueprint (landing "Nuova automazione"): da qui si consulta o si "usa" un template.
  { path: '/automations/templates', element: <AutomationGallery /> },
  // Builder: crea un nuovo TEMPLATE.
  { path: '/automations/templates/new', element: <AutomationForm /> },
  // View SOLA LETTURA del template (qualunque stato). L'azione "Usa template" (crea un'automazione da
  // qui) porterà a /automations/:id/edit del nuovo record.
  { path: '/automations/templates/:id', element: <AutomationForm /> },
  // Edit del template (solo DRAFT): se non è DRAFT, redirect alla view read-only del template.
  { path: '/automations/templates/:id/edit', element: <AutomationForm /> },
  { path: '/automations/templates/:id/simulation', element: <AutomationSimulation /> },
  { path: '/profile', element: <ProfilePage /> },
  { path: '/settings', element: <SettingsPage /> },
  { path: '/settings/user', element: <UserSettingsPage /> },
  // "Utenti e team" e' passato alle impostazioni dell'applicazione: il vecchio
  // indirizzo resta come redirect, altrimenti un link salvato cadrebbe in
  // silenzio su "Gestione Account" (il default di UserSettingsPage). Il segmento
  // statico vince sul ':section' qui sotto, indipendentemente dall'ordine.
  { path: '/settings/user/teams', element: <Navigate to="/settings/teams" replace /> },
  { path: '/settings/user/:section', element: <UserSettingsPage /> },
  // ─── Pipeline (config): sotto Impostazioni ───
  // L'ELENCO vive in SettingsPage (sezione 'pipelines', catturata da
  // /settings/:section qui sotto); qui restano solo i FORM di creazione/modifica.
  // I segmenti statici vincono sul dinamico :section, quindi questi due path non
  // collidono con /settings/:section. '/new' statico vince su ':id'.
  { path: '/settings/pipelines/new', element: <PipelineForm /> },
  { path: '/settings/pipelines/:id/edit', element: <PipelineForm /> },
  // Le voci di una picklist: stessa pagina delle Impostazioni, PicklistSettings legge l'id.
  { path: '/settings/gestione-picklist/:listTypeDefinitionId', element: <SettingsPage /> },
  // Sotto-sezioni delle Impostazioni (es. /settings/queues, /settings/pipelines)
  // gestite dentro SettingsPage via param :section. Nota: /settings/user[/...] e
  // /settings/pipelines/* restano sopra e vincono su questo path dinamico.
  { path: '/settings/:section', element: <SettingsPage /> },
];

export const fallbackRoutes: RouteObject[] = [
  { path: '*', element: <NotFoundPage /> },
];
